import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HubHeader from "../HubHeader";
import HubSidebar from "../HubSidebar";
import CreateNewAMP from "./CreateNewAMP";
import HubLoader from "../../../common/HubLoader";
import { FaPlus } from "react-icons/fa";
import { IconType, IconBaseProps } from "react-icons";
import { getAllAMPs } from "./ampConfigs";
import "../../../assets/css/Hub.css";

type AmpCard = {
  id: string;
  title: string;
  description: string;
  icon: IconType;
  color: string;
};

export default function AMPLandingPage() {
  const [collapsed, setCollapsed] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [ampCards, setAmpCards] = useState<AmpCard[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch all AMPs (predefined + user-created)
  useEffect(() => {
    const loadAMPs = async () => {
      setLoading(true);
      const amps = await getAllAMPs();
      // Add delay to show the loader
      await new Promise(resolve => setTimeout(resolve, 1500));
      setAmpCards(amps);
      setLoading(false);
    };
    
    loadAMPs();
  }, []);

  const handleCreateOpen = () => setShowCreate(true);
  const handleCreateClose = () => setShowCreate(false);

  const handleCreateSubmit = (ampData: {
    name: string;
    description: string;
    instructions: string;
    knowledge: string;
    knowledgeFile: File | null;
  }) => {
    // TODO: Send to backend API to create user AMP
    // POST /api/amps with ampData
    console.log('Create AMP:', ampData);
    handleCreateClose();
    // Reload AMPs after creation
    getAllAMPs().then(setAmpCards);
  };

  const handleCollapseToggle = (next: boolean) => setCollapsed(next);

  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const handleCardClick = (cardId: string) => {
    const guid = generateGuid();
    navigate(`/amp/${cardId}/${guid}`);
  };

  return (
    <div className="supportbot-landing-layout">
      <HubHeader />

      <div className="supportbot-landing-body">
        <aside className="supportbot-sidebar-wrap taskbar-only" aria-label="AMP navigation">
          <HubSidebar pagename="amp-landing" collapsed={collapsed} onCollapseToggle={handleCollapseToggle} />
        </aside>
        <main className="supportbot-main-content full-width">
          {!showCreate && (
            <header className="amp-header-row">
              <div>
                <h1 className="amp-main-title">AMPs</h1>
                <p style={{ margin: '8px 0 0 0', fontSize: '1rem', color: '#6b7280', fontWeight: 500 }}>
                  AI-powered modules to enhance your workflow
                </p>
              </div>
              <button className="hub-btn hub-btn-signup" onClick={handleCreateOpen} aria-haspopup="dialog">
                {React.createElement(FaPlus as any, { style: { fontSize: '14px' } })}
                Create AMP
              </button>
            </header>
          )}

          <div className="amp-cards-scroll-wrapper" role="region" aria-label="AMP list">
            {showCreate ? (
              <CreateNewAMP 
                onClose={handleCreateClose} 
                onSubmit={handleCreateSubmit}
              />
            ) : loading ? (
              <HubLoader message="Loading your AMPs..." size="large" />
            ) : (
              <section className="amp-cards-container crisp-row" aria-live="polite">{ampCards.map((card) => {
                  const Icon = card.icon as React.FC<IconBaseProps>;
                  return (
                    <article 
                      key={card.id} 
                      className="amp-card unique-card" 
                      aria-labelledby={`card-${card.id}`} 
                      role="article"
                      onClick={() => handleCardClick(card.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="amp-card-icon-wrap" aria-hidden>
                        <Icon className="amp-card-icon" color={card.color} />
                      </div>
                      <div className="amp-card-content">
                        <h3 id={`card-${card.id}`}>{card.title}</h3>
                        <p>{card.description}</p>
                      </div>
                    </article>
                  );
                })}
              </section>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
